import {
  GridApi,
  GridOptions,
  IServerSideDatasource,
} from "@ag-grid-community/core";

import { Component, Input, OnChanges, OnInit, OnDestroy } from "@angular/core";
import { PriceyardService } from "src/app/modules/priceyard/services/priceyard.service";
import { DateCellRendererComponent } from "src/app/ag-grid-components/date-cell-renderer/date-cell-renderer.component";
import { LoadingCellRendererComponent } from "src/app/ag-grid-components/loading-cell-renderer/loading-cell-renderer.component";
import { SkuCellRendererComponent } from "src/app/ag-grid-components/sku-cell-renderer/sku-cell-renderer.component";
import { SkuDetail } from "./../../../../models/priceyard.model";
import { HeaderService } from "./../../../../components/header/header.service";
import { NoteCellRendererComponent } from "src/app/ag-grid-components/note-cell-renderer/note-cell-renderer.component";
import { Router } from "@angular/router";
import { AvalableDateLabels, DatePickerModel } from "src/app/components/date-picker-dialog/date-picker.model";
import { DatePipe} from "@angular/common";
import { DashboardService } from "src/app/services/dashboard.service";
import { Subject, takeUntil } from "rxjs";

@Component({
  selector: "app-sku-history-grid",
  templateUrl: "./sku-history-grid.component.html",
  styleUrls: ["./sku-history-grid.component.scss", "../mini-grid.scss"],
})
export class SkuHistoryGridComponent implements OnInit, OnChanges,OnDestroy {
  @Input() sku: SkuDetail;
  gridApi: GridApi;
  private destroy$ = new Subject<void>();
  columnDefs = [
    {
      field: "ActionDate",
      cellRenderer: "dateCellRendererComponent",
      filter: "agDateColumnFilter",
    },
    {
      field: "NewPrice",
      valueFormatter: (params) =>
        this.priceyardService.currencyFormatter(params.data.NewPrice, "$"),
      filter: "agNumberColumnFilter",
    },
    {
      field: "PriceMove",
      valueFormatter: (params) =>
        this.priceyardService.currencyFormatter(params.data.PriceMove, "$"),
      filter: "agNumberColumnFilter",
      cellStyle: (params) => {
        if (Math.sign(params.value) === 1) {
          return { color: "green" };
        } else if (Math.sign(params.value) === -1) {
          return { color: "red" };
        }
        return null;
      },
    },
    {
      field: "Note",
      filter: "agTextColumnFilter",
      cellRenderer: "noteCellRendererComponent",
      cellClass: "cell-vertical-align-text-center",
    },
  ];
  components = {
    loadingCellRenderer: LoadingCellRendererComponent,
    dateCellRendererComponent: DateCellRendererComponent,
    skuCellRendererComponent: SkuCellRendererComponent,
    noteCellRendererComponent: NoteCellRendererComponent,
  };
  gridOptions: GridOptions = {
    rowModelType: "serverSide",
    suppressServerSideInfiniteScroll: false,
    suppressCopyRowsToClipboard: true,
    cacheBlockSize: 100,
    defaultColDef: {
      resizable: true,
      sortable:false,
      filterParams: {
        maxNumConditions : 1,
      },
      menuTabs: ["filterMenuTab"],
    },
    columnDefs: this.columnDefs,
    components: this.components,
    rowSelection: "multiple",
    loadingCellRenderer: "loadingCellRenderer",
    onGridReady: (e) => {
      this.gridApi = e.api;
      this.headerService.accountsSelected.pipe(takeUntil(this.destroy$)).subscribe((e) => {
        this.gridApi.setGridOption("serverSideDatasource", this.dataSource);
        this.gridApi.showLoadingOverlay();
        this.gridApi.sizeColumnsToFit();
      });
    },
  };
  dataSource: IServerSideDatasource = {
    getRows: (params: any) => {
      params.request.filterModel.ActionDate = {dateFrom: this.datePipe.transform(this.dateFrom, 'yyyy-MM-ddT00:00:00'),
         dateTo : this.dateTo, filterType :  "date", type: "inRange"}
      this.priceyardService
        .SkuRepriceHistoryGrid(params.request, this.columnDefs, "", {
          field: "Sku",
          filter: this.sku?.sku,
        }).pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: any) => {
            this.spin = false;
            this.dialogRef?.close();
            if (response) {
              if (!response.totalCount) {
                this.gridApi.showNoRowsOverlay();
              } else {
                this.gridApi.hideOverlay();
              }
              params.success({
                rowData: response.data,
                rowCount: response.totalCount,
              });
            }
          },
          error: (err) => {
            params.fail()
            this.spin = false;
            this.dialogRef?.close();
          }}
        );
    },
  };
  dateFilterButtonText: string = "Last 30 Days";
  dateFilter: DatePickerModel = {
    from: new Date(new Date().setDate(new Date().getDate() - 30)),
    to: new Date(),
    label: AvalableDateLabels.Last30Days,
  };
  dateFrom = new Date();
  dateTo= new Date(new Date().getTime() - this.getDaysAgo(30));
  currentDateFilter: DatePickerModel;

  isSidePanel: boolean;
  spin;
  dialogRef;
  constructor(
    private priceyardService: PriceyardService,
    private headerService: HeaderService,
    private router: Router,
    private datePipe: DatePipe,
    private dashboardService: DashboardService
  ) {
    this.dashboardService.selectedDate$.pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
      if (res) {
        this.spin = res?.spinner;
        this.dialogRef = res?.dialogRef;     
        this.dateFrom = res.startDate;
        this.dateTo = res.endDate;
        if(this.gridApi) this.gridApi.setGridOption("serverSideDatasource", this.dataSource);
      }
    }),
    this.isSidePanel = localStorage.getItem("toolPanel") === "true";
  }

  resized(e) {
    this.gridApi.sizeColumnsToFit();
  }

  ngOnChanges(): void {
    if (this.gridApi) {
      this.gridApi.setGridOption("serverSideDatasource", this.dataSource);
    }
  }

  ngOnInit(): void {
    const today = new Date();
    this.currentDateFilter={
      to: today,
      from:new Date(today.getTime() - this.getDaysAgo(30)),
      label:'Last 30 Days'
    }
  }

  navigate(): void {
    this.router.navigate(["priceyard/history"], {
      queryParams: { sku: this.sku.sku },
    });
  }

  getDaysAgo(amountOfDays: number): number {
    return amountOfDays * 24 * 60 * 60 * 1000;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }
}
